// ═══════════════════════════════════════════════════════════════════════
// userSyncGuideTab.jsx — User sync guide reader
// ═══════════════════════════════════════════════════════════════════════
// Operational sync guide: how data synchronizes across CLM systems.
// Source: CLM_UserSyncGuide.pptx + CLM_Sync_Strategy.pptx
// Persona-organized: Engineer, Planner, Operator, Quality, Finance, IT.
// ═══════════════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { T, FONTS } from "./theme";

const PERSONAS = [
  {
    id: "ENG", name: "Engineer", icon: "👷", color: T.cyan,
    summary: "You design products in Teamcenter. Your changes flow to manufacturing and finance — and back.",
    flows: [
      { trigger: "You release an ECN",
        whatHappens: "AIG raises an event within 60s. ESB fans out to Opcenter (effectivity), SAP (material/STKO), Insights Hub.",
        whenItLands: "MES sees the change in < 90 seconds. SAP material master updated within 5 minutes.",
        whatYouSee: "Status badge on the ECN turns from 'Propagating' to 'Live'. Check the right-side activity feed.",
        ifSomethingFails: "DLQ message appears on your dashboard with the failure reason. Click 'Replay' to retry, or open a ticket." },
      { trigger: "You change a BOM structure",
        whatHappens: "Structure changes go through Architecture Review. After approval, T4S syncs STKO/STPO to SAP.",
        whenItLands: "SAP receives within 5 minutes. Opcenter routing follows.",
        whatYouSee: "BOM in TC shows 'In-Sync' badge once all downstream confirms received.",
        ifSomethingFails: "Reconciliation diff visible on the BOM view. Quality team is alerted automatically." },
      { trigger: "You add a safety-critical characteristic",
        whatHappens: "Bright-line gate: a named human approver must sign before the change leaves PLM.",
        whenItLands: "Approval queues to Quality manager. They review evidence and sign.",
        whatYouSee: "Yellow pending status until approver signs. Cannot bypass.",
        ifSomethingFails: "Quality manager attaches comment. You edit & resubmit." },
    ],
  },
  {
    id: "PLAN", name: "Planner", icon: "📋", color: T.amber,
    summary: "You sequence work. APS optimizes; SAP holds the demand; Opcenter executes. The system synchronizes for you.",
    flows: [
      { trigger: "Customer order arrives in SAP",
        whatHappens: "AUFNR created in SAP. APS receives demand. You sequence in APS or accept the auto-sequence.",
        whenItLands: "Released work orders reach Opcenter within 30 seconds.",
        whatYouSee: "Order appears on the dispatch board with timestamps showing when each system confirmed.",
        ifSomethingFails: "If Opcenter rejects (capacity/skill mismatch), the order goes to your 'Exceptions' queue." },
      { trigger: "Production confirmation lands",
        whatHappens: "Operator confirms operation in Opcenter. ESB carries to SAP AFRU + Insights Hub OEE.",
        whenItLands: "P99 < 30 seconds end-to-end · exactly-once on finance side.",
        whatYouSee: "Order progress bar advances. KPI dashboard refreshes within 5 seconds.",
        ifSomethingFails: "Variance Analyst agent (if enabled) drafts an explanation. Review and approve narrative." },
    ],
  },
  {
    id: "OPER", name: "Operator", icon: "🛠️", color: T.orange,
    summary: "You work from the SFC terminal. Work instructions come down; confirms go up; quality and time data flow automatically.",
    flows: [
      { trigger: "You log on and start a job",
        whatHappens: "Opcenter sends work instructions and BoP details to the SFC terminal.",
        whenItLands: "Instantly — your screen shows the current step.",
        whatYouSee: "Live work instruction with images, tools, torque values from PLM.",
        ifSomethingFails: "Andon button pages your supervisor within 5 minutes (target)." },
      { trigger: "You confirm a step complete",
        whatHappens: "Step confirmation → Opcenter execution → ESB → SAP AFRU + Insights Hub OEE + audit ledger.",
        whenItLands: "All systems see it within 30 seconds.",
        whatYouSee: "Next step appears. KPI updates. Operator dashboard shows you're on takt or behind.",
        ifSomethingFails: "Your SFC terminal locally buffers. When connection returns, it syncs in order." },
      { trigger: "You raise an NCR",
        whatHappens: "NCR template prefilled from PLM characteristics. Containment scope determined by config.",
        whenItLands: "Quality team paged within 60 seconds. TC change request opened if needed.",
        whatYouSee: "Containment hold applied to affected lots immediately. Audit log entry created.",
        ifSomethingFails: "Triage agent (if enabled) routes to backup responder if primary doesn't acknowledge." },
    ],
  },
  {
    id: "QUAL", name: "Quality", icon: "🔬", color: T.pink,
    summary: "You own NCR resolution, CAPA closure, audit readiness. Floor signals come to you; design changes flow from you.",
    flows: [
      { trigger: "NCR arrives from the floor",
        whatHappens: "NCR Triage agent (if L2) classifies and recommends disposition. You review or override.",
        whenItLands: "Critical NCRs page you within 60s. Routine ones queued in your inbox.",
        whatYouSee: "NCR with photos, measurements, lot genealogy, suggested disposition.",
        ifSomethingFails: "Material hold remains in place until you disposition. No release without sign-off." },
      { trigger: "You close a CAPA that requires a design change",
        whatHappens: "CAPA closure triggers a change request to PLM. Engineering owns the ECN; you sign quality acceptance.",
        whenItLands: "Change request in PLM within 60 seconds.",
        whatYouSee: "Linked thread: NCR → CAPA → ECN → effectivity → confirmation. End-to-end traceable.",
        ifSomethingFails: "If ECN never lands in PLM, the audit ledger flags this and you're paged." },
      { trigger: "Audit prep request",
        whatHappens: "Audit Preparer agent (if enabled) assembles evidence bundles. You review and sign.",
        whenItLands: "Bundle ready within 24h for routine audit; 4h for emergency.",
        whatYouSee: "All required evidence: lot genealogy, measurement records, signatures, deviations.",
        ifSomethingFails: "Missing evidence flagged with red — the system tells you what to gather manually." },
    ],
  },
  {
    id: "FIN", name: "Finance", icon: "💼", color: T.gold,
    summary: "You own GL postings, variance analysis, period close. Production data flows to you continuously and reconciles by design.",
    flows: [
      { trigger: "Production confirmation posts to SAP",
        whatHappens: "AFRU posting includes labor, scrap, output. Cost flows to CO automatically.",
        whenItLands: "P99 < 30s · exactly-once delivery on this flow.",
        whatYouSee: "Cost variance dashboard updates in near real-time. Period close is a continuous activity.",
        ifSomethingFails: "Dual-write protection (outbox pattern) means SAP and audit ledger never disagree." },
      { trigger: "Period close kicks off",
        whatHappens: "Period Close Helper agent (if L2) runs validation checks, drafts journals, catches anomalies pre-close.",
        whenItLands: "Pre-close validation in hours, not days.",
        whatYouSee: "Close checklist with green/red status per item. Anomaly explanations from Variance Analyst.",
        ifSomethingFails: "Reconciliation gaps flagged with linked transactions. Audit trail accompanies every adjustment." },
    ],
  },
  {
    id: "IT", name: "IT / Integration", icon: "🔌", color: T.blue,
    summary: "You own the bus, the gateway, the IdP, the DLQ. Your KPIs are availability and MTTR. Operators are your customers.",
    flows: [
      { trigger: "DLQ message appears",
        whatHappens: "DLQ Triage agent (if L3) classifies. Safe ones auto-replay; rest routed to flow owner.",
        whenItLands: "Auto-replay within 1 minute. Routing within 5 minutes.",
        whatYouSee: "DLQ dashboard with per-flow owner, classification, replay history.",
        ifSomethingFails: "If pattern repeats > 3 times in 1 hour, SLA Watchdog pages on-call." },
      { trigger: "Schema change deployed",
        whatHappens: "Schema registry validates BACKWARD compatibility. If breaking, deployment blocked.",
        whenItLands: "Validation in seconds at CI time. Production deploy only after pass.",
        whatYouSee: "Schema diff in PR. Compatibility check status as required gate.",
        ifSomethingFails: "Deploy fails fast. Producers and consumers never drift." },
      { trigger: "P1 incident",
        whatHappens: "SLA Watchdog detects burn-rate. Runbook auto-linked. On-call paged with context.",
        whenItLands: "Detection < 60s. Page < 2 min. Target MTTR ≤ 30 min for P1.",
        whatYouSee: "Incident channel + runbook + last-known-good config + change history.",
        ifSomethingFails: "Kill-switch always one click away. Pause → Stop → Revert → Disable." },
    ],
  },
];

const PRINCIPLES = [
  { icon: "🎯", title: "Single source of truth per entity",
    detail: "Each business entity has ONE owner system. Others subscribe — never write back." },
  { icon: "⏱️", title: "Asynchronous by default",
    detail: "No sync API calls across the bus. Every cross-system action is event-driven with ACKs." },
  { icon: "🔁", title: "Idempotent everywhere",
    detail: "Receive twice, apply once, same outcome. Built into every consumer pattern." },
  { icon: "📝", title: "Audit before action",
    detail: "Every cross-system change is written to the audit ledger BEFORE it's applied downstream." },
  { icon: "🛡️", title: "Bright lines protect humans",
    detail: "12 inviolable gates: safety-critical chars, BOM structure, customer comms, regulatory — always human." },
  { icon: "🔍", title: "Observability is non-negotiable",
    detail: "Every message has a correlation_id. Every hop is traced. RCA goes from days to minutes." },
];

export default function UserSyncGuideTab() {
  const [persona, setPersona] = useState("ENG");
  const [expanded, setExpanded] = useState(new Set());

  const toggleExpand = (key) => setExpanded(prev => {
    const ns = new Set(prev);
    if (ns.has(key)) ns.delete(key); else ns.add(key);
    return ns;
  });

  const p = PERSONAS.find(x => x.id === persona);

  return (
    <div style={{ padding: "20px 24px", maxHeight: "calc(100vh - 108px)", overflowY: "auto" }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: FONTS.header, fontSize: 16, fontWeight: 700, color: T.cyan, marginBottom: 4 }}>
          👥 User Sync Guide
        </div>
        <div style={{ fontSize: 11, color: T.text2 }}>
          How CLM systems synchronize — by role · what triggers what · when it lands · what you see · what to do when it fails
        </div>
      </div>

      {/* 6 principles */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.amber, letterSpacing: 1, marginBottom: 10, textTransform: "uppercase" }}>
          🎯 Six sync principles
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
          {PRINCIPLES.map(pr => (
            <div key={pr.title} style={{
              background: T.bg2, border: `1px solid ${T.amber}30`,
              borderLeft: `3px solid ${T.amber}`, borderRadius: 6, padding: "10px 12px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 16 }}>{pr.icon}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: T.amber }}>{pr.title}</span>
              </div>
              <div style={{ fontSize: 9, color: T.text2, lineHeight: 1.5 }}>{pr.detail}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Persona tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {PERSONAS.map(per => (
          <button key={per.id} onClick={() => setPersona(per.id)}
            style={{
              padding: "8px 14px", borderRadius: 6, cursor: "pointer",
              background: persona === per.id ? `${per.color}15` : T.bg3,
              border: `1px solid ${persona === per.id ? per.color : T.border}`,
              color: persona === per.id ? per.color : T.text2,
              fontWeight: persona === per.id ? 700 : 400, fontSize: 11,
              fontFamily: FONTS.mono, letterSpacing: 1,
              display: "flex", alignItems: "center", gap: 6,
            }}>
            <span style={{ fontSize: 14 }}>{per.icon}</span>
            {per.name}
          </button>
        ))}
      </div>

      {/* Persona detail */}
      <div style={{
        background: T.bg2, border: `1px solid ${p.color}40`,
        borderLeft: `4px solid ${p.color}`, borderRadius: 8, padding: "18px 22px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <span style={{ fontSize: 36 }}>{p.icon}</span>
          <div>
            <div style={{ fontFamily: FONTS.header, fontSize: 18, fontWeight: 700, color: p.color }}>
              {p.name}
            </div>
            <div style={{ fontSize: 10.5, color: T.text2, fontStyle: "italic", marginTop: 4 }}>
              {p.summary}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          {p.flows.map((f, i) => {
            const key = `${p.id}-${i}`;
            const isOpen = expanded.has(key);
            return (
              <div key={i} style={{
                background: T.bg3, border: `1px solid ${p.color}25`,
                borderRadius: 6, marginBottom: 10, overflow: "hidden",
              }}>
                <div onClick={() => toggleExpand(key)} style={{
                  padding: "12px 16px", cursor: "pointer",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  borderBottom: isOpen ? `1px solid ${p.color}25` : "none",
                }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>
                    <span style={{ color: p.color, marginRight: 8 }}>▶</span> {f.trigger}
                  </span>
                  <span style={{
                    color: p.color, fontSize: 14,
                    transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s",
                  }}>▸</span>
                </div>
                {isOpen && (
                  <div style={{ padding: "12px 16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    {[
                      { label: "🔄 What happens",        v: f.whatHappens,      color: p.color },
                      { label: "⏰ When it lands",        v: f.whenItLands,      color: T.green },
                      { label: "👁 What you see",         v: f.whatYouSee,       color: T.cyan },
                      { label: "⚠️ If something fails",  v: f.ifSomethingFails, color: T.amber },
                    ].map(item => (
                      <div key={item.label} style={{
                        padding: "8px 10px", background: T.bg2,
                        border: `1px solid ${item.color}20`, borderRadius: 4,
                      }}>
                        <div style={{ fontSize: 8.5, color: item.color, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>
                          {item.label}
                        </div>
                        <div style={{ fontSize: 9.5, color: T.text2, lineHeight: 1.5 }}>{item.v}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div style={{
        marginTop: 20, padding: "12px 16px",
        background: T.bg2, border: `1px solid ${T.cyan}30`, borderRadius: 6,
        fontSize: 10, color: T.cyan, fontStyle: "italic",
      }}>
        💡 Everything you see here is governed by the data contracts (see Data Contracts tab) and delivered by the ESB (see ESB tab).
        If you're seeing a delay longer than what's listed here, check the SLA Watchdog dashboard or open a ticket — there's likely an incident.
      </div>
    </div>
  );
}
